import { Bell, Coins, Ruler, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { AccountMenu } from '@/components/AccountMenu';
import { formatCredits } from '@/lib/utils';
import { AppView } from '@/types';
import type { SettingsTab } from '@/components/views/SettingsView';

interface TopBarProps {
  title: string | null;
  subtitle: string | null;
  credits: number;
  floorplanUnits: number;
  isLoadingCredits: boolean;
  unreadCount: number;
  email: string;
  avatarUrl: string | null;
  disabledFeatures: string[];
  isAdmin: boolean;
  hasTeam: boolean;
  plan: 'starter' | 'pro' | 'agency' | null;
  subscriptionStatus: string | null;
  onNavigate: (view: AppView) => void;
  onNavigateSettings: (tab: SettingsTab) => void;
  onOpenWhatsNew: () => void;
  onSignOut: () => void;
}

export const TopBar: React.FC<TopBarProps> = (p) => {
  const creditLabel = p.isLoadingCredits ? '…' : formatCredits(p.credits);
  const floorplanLabel = p.isLoadingCredits ? '…' : formatCredits(p.floorplanUnits);
  const hasActiveSub = p.subscriptionStatus === 'active' && !!p.plan;
  const planLabel = p.plan ? p.plan.charAt(0).toUpperCase() + p.plan.slice(1) : null;
  return (
    <header className="sticky top-0 z-30 flex h-[60px] flex-shrink-0 items-center justify-between gap-3 border-b border-border bg-background px-4 sm:px-6">
      {/* Left: page header (dashboard) or wordmark */}
      <div className="flex min-w-0 items-center">
        {p.title ? (
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-[17px] font-semibold tracking-[-0.01em] text-foreground">{p.title}</span>
            {p.subtitle && <span className="truncate text-xs text-muted-foreground">{p.subtitle}</span>}
          </div>
        ) : (
          <span className="text-lg font-bold tracking-tight">estage<span className="text-primary">.ai</span></span>
        )}
      </div>

      {/* Right: credits + news + account */}
      <div className="flex items-center gap-3">
        {hasActiveSub && (
          <Badge className="hidden sm:inline-flex bg-primary/10 text-primary border-primary/20 font-semibold">
            {planLabel}-Abo
          </Badge>
        )}
        <div className="relative flex items-stretch rounded-full border border-border bg-card">
          <button
            onClick={() => p.onNavigate('credits')}
            className="flex items-center gap-2 rounded-l-full py-1.5 pl-2 pr-2.5 text-sm font-bold hover:bg-muted"
          >
            <span className="group/bild relative flex items-center gap-1.5">
              <span className="grid h-5 w-5 place-items-center rounded-full bg-primary/10 text-primary"><Coins className="h-3 w-3" /></span>
              <span className="tabular-nums">{creditLabel}</span>
              <span className="pointer-events-none absolute top-full left-1/2 z-50 mt-2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] font-normal text-background shadow-md group-hover/bild:block">
                Bild-Credits — Staging, Leerräumen &amp; Upscale
              </span>
            </span>
            <span className="text-muted-foreground/50">·</span>
            <span className="group/gr relative flex items-center gap-1.5">
              <span className="grid h-5 w-5 place-items-center rounded-full bg-primary/10 text-primary"><Ruler className="h-3 w-3" /></span>
              <span className="tabular-nums">{floorplanLabel}</span>
              <span className="pointer-events-none absolute top-full left-1/2 z-50 mt-2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] font-normal text-background shadow-md group-hover/gr:block">
                Grundriss-Einheiten — 2D/3D-Grundrisse
              </span>
            </span>
          </button>
          <button
            onClick={() => p.onNavigate('credits')}
            aria-label={hasActiveSub ? 'Abo verwalten' : 'Abo wählen'}
            className="flex items-center gap-1 rounded-r-full border-l border-primary-foreground/20 bg-primary px-3 text-[13.5px] font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-3.5 w-3.5" /><span className="hidden sm:inline">{hasActiveSub ? 'Abo' : 'Abo wählen'}</span>
          </button>
        </div>

        <button
          onClick={p.onOpenWhatsNew}
          className="relative grid h-10 w-10 place-items-center rounded-full border border-border bg-card hover:bg-muted"
          aria-label="Neuigkeiten"
        >
          <Bell className="h-[19px] w-[19px]" />
          {p.unreadCount > 0 && (
            <Badge variant="destructive" className="absolute -top-1.5 -right-1.5 h-5 min-w-[1.25rem] rounded-full border-2 border-background px-1 text-[10px] leading-none tabular-nums">
              {p.unreadCount > 9 ? '9+' : p.unreadCount}
            </Badge>
          )}
        </button>

        <AccountMenu
          email={p.email}
          avatarUrl={p.avatarUrl}
          credits={p.credits}
          floorplanUnits={p.floorplanUnits}
          isLoadingCredits={p.isLoadingCredits}
          disabledFeatures={p.disabledFeatures}
          isAdmin={p.isAdmin}
          hasTeam={p.hasTeam}
          plan={p.plan}
          subscriptionStatus={p.subscriptionStatus}
          onNavigate={p.onNavigate}
          onNavigateSettings={p.onNavigateSettings}
          onSignOut={p.onSignOut}
        />
      </div>
    </header>
  );
};
